/**
 * QA checklist editor — assessment options from the portal-aligned library,
 * labels via cerPortalOptionText so the three case-trap severities match the
 * CER portal option text (not ASSESSMENT_OPTIONS.label).
 *
 * Changes stay in the parent FieldReport draft until Save as new revision.
 */

import { useEffect, useRef, useState } from 'react';
import {
  applyAssessment,
  assessmentOptionsForClause,
  assessmentRequiresWording,
  assessmentValueForClauseResponse,
  cerPortalOptionText,
  findAssessmentEntry,
  isCustomResponse,
  type ChecklistSection,
  type ClauseResponse,
  type FieldReport,
  type PhotoQuarterTurn,
  type StoredPhotoManifest,
} from '@cer-autofill/shared';
import {
  isRoutineChecklistResponse,
  reportChecklistRows,
  updateReportClause,
  type ReportChecklistRow,
} from '../lib/report-checklist.js';
import { EditablePhotoTile } from './EditablePhotoTile.js';
import { FilingPicker, type FilingTargetGroup } from './FilingPicker.js';
import { ReportPhotoEditor, type ReportPhotoActions } from './ReportPhotoEditor.js';

const SECTION_LABEL: Record<ChecklistSection, string> = {
  'cer-pv': 'CER PV',
  'battery-section-4-ac': 'Battery — Section 4 (AC coupled)',
  'battery-section-5-dc': 'Battery — Section 5 (DC coupled)',
};

function portalAssessmentLabel(value: string, fallbackLabel: string): string {
  // Prefer the portal's option text. Falling back to the UI label is only for
  // values with no confirmed portal equivalent (non_regulatory, not_viewed…).
  return cerPortalOptionText(value) ?? fallbackLabel;
}

export function ReportChecklistEditor({
  draft,
  onChange,
  showRoutine: showRoutineProp,
  onShowRoutine,
  focusKey,
  onUploadPhoto,
  onDeletePhoto,
  onRotatePhoto,
  onRefilePhoto,
  onToggleRefile,
  photos = [],
  onOpenPhoto = () => undefined,
  canManagePhotos = false,
  photoActionBusyId = null,
  refilingPhotoId = null,
  filingTargets = [],
}: {
  draft: FieldReport;
  onChange: (next: FieldReport) => void;
  /**
   * Shared with the report sidebar. When omitted, the editor keeps its own
   * checkbox state.
   */
  showRoutine?: boolean;
  onShowRoutine?: (value: boolean) => void;
  /**
   * `${section}:${clauseId}` of the clause a QA deep-link wants fixed —
   * scrolled into view and flashed once when the editor opens.
   */
  focusKey?: string;
  photos?: readonly StoredPhotoManifest[];
  onOpenPhoto?: (photo: StoredPhotoManifest, url: string) => void;
  /** Gallery rotate, re-file and delete. Reviewers only; the routes require QA. */
  canManagePhotos?: boolean;
  photoActionBusyId?: string | null;
  refilingPhotoId?: string | null;
  filingTargets?: readonly FilingTargetGroup[];
  onToggleRefile?: (photoId: string) => void;
  onRefilePhoto?: (photoId: string, promptKey: string) => Promise<void> | void;
} & ReportPhotoActions) {
  const [ownShowRoutine, setOwnShowRoutine] = useState(false);
  const showRoutine = showRoutineProp ?? ownShowRoutine;
  const setShowRoutine = onShowRoutine ?? setOwnShowRoutine;
  const focusRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = focusRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('jump-flash');
  }, [focusKey]);

  const bySection = reportChecklistRows(draft);

  return (
    <div className="edit-form-section">
      <h4>Checklist</h4>
      <label>
        <input
          type="checkbox"
          checked={showRoutine}
          onChange={(e) => setShowRoutine(e.target.checked)}
        />{' '}
        Show compliant, not sighted and not applicable items
      </label>
      <p className="muted">
        Questions follow the current checklist order. Unanswered items stay blank until you choose
        an assessment. Existing answers and notes are preserved.
      </p>
      {canManagePhotos && (
        <p className="muted">
          Rotating, re-filing and deleting a checklist photo saves immediately. Delete asks first.
        </p>
      )}
      {Array.from(bySection.entries()).map(([section, responses]) => {
        const visible = responses.filter(
          (row) =>
            showRoutine ||
            focusKey === `${section}:${row.clauseId}` ||
            !isRoutineChecklistResponse(row.response),
        );
        if (visible.length === 0) return null;
        return (
          <div key={section} className="edit-checklist-section">
            <h5>{SECTION_LABEL[section]}</h5>
            {visible.map((row) => (
              <div
                key={`${section}:${row.clauseId}`}
                ref={focusKey === `${section}:${row.clauseId}` ? focusRef : undefined}
              >
                <ClauseEditorRow
                  row={row}
                  onUploadPhoto={onUploadPhoto}
                  onDeletePhoto={onDeletePhoto}
                  {...(onRotatePhoto ? { onRotatePhoto } : {})}
                  {...(onRefilePhoto ? { onRefilePhoto } : {})}
                  {...(onToggleRefile ? { onToggleRefile } : {})}
                  photos={photos}
                  jobId={draft.jobId}
                  onOpenPhoto={onOpenPhoto}
                  canManagePhotos={canManagePhotos}
                  photoActionBusyId={photoActionBusyId}
                  refilingPhotoId={refilingPhotoId}
                  filingTargets={filingTargets}
                  onChange={(response) => onChange(updateReportClause(draft, response))}
                />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function ClauseEditorRow({
  row,
  onUploadPhoto,
  onDeletePhoto,
  onRotatePhoto,
  onRefilePhoto,
  onToggleRefile,
  onChange,
  photos,
  jobId,
  onOpenPhoto,
  canManagePhotos,
  photoActionBusyId,
  refilingPhotoId,
  filingTargets,
}: {
  row: ReportChecklistRow;
  onChange: (response: ClauseResponse) => void;
  photos: readonly StoredPhotoManifest[];
  jobId: string;
  onOpenPhoto: (photo: StoredPhotoManifest, url: string) => void;
  canManagePhotos: boolean;
  photoActionBusyId: string | null;
  refilingPhotoId: string | null;
  filingTargets: readonly FilingTargetGroup[];
  onToggleRefile?: (photoId: string) => void;
  onRefilePhoto?: (photoId: string, promptKey: string) => Promise<void> | void;
} & ReportPhotoActions) {
  const { response, section, clauseId } = row;
  const libraryId = row.libraryId ?? clauseId;
  const options = assessmentOptionsForClause(section, libraryId);
  const selected = response ? (assessmentValueForClauseResponse(response) ?? '') : '';
  const needsWording = assessmentRequiresWording(selected || undefined);
  const customResponse = isCustomResponse(section, libraryId, response?.recommendation);
  const wordingOptions = findAssessmentEntry(section, libraryId)?.wordingOptions ?? [];
  const onPatch = (patch: (current: ClauseResponse) => ClauseResponse) => {
    if (response) onChange(patch(response));
  };
  const evidence = photos.filter(
    (photo) =>
      response?.photoIds.includes(photo.photoId) ||
      photo.promptKey.toLowerCase() === `clause:${clauseId}`.toLowerCase(),
  );

  return (
    <div className="edit-clause-row">
      <div className="edit-clause-row__head">
        <strong>{clauseId.replace(/([A-Za-z])\s+(?=\d)/g, '$1')}</strong>
        <span className="muted">{response?.outcome ?? 'Unanswered'}</span>
      </div>
      {row.question && <p>{row.question}</p>}
      {evidence.length > 0 && (
        <div className="photo-grid photo-grid--editable">
          {evidence.map((photo) => (
            <EditablePhotoTile
              key={photo.photoId}
              manifest={photo}
              caption={photo.filename}
              canManage={canManagePhotos}
              busy={photoActionBusyId === photo.photoId}
              refiling={refilingPhotoId === photo.photoId}
              onOpen={(url) => onOpenPhoto(photo, url)}
              onToggleRefile={() => onToggleRefile?.(photo.photoId)}
              onRotate={(degrees: PhotoQuarterTurn) => {
                void onRotatePhoto?.(photo.photoId, degrees);
              }}
              onDelete={() => {
                void onDeletePhoto?.(photo.photoId);
              }}
              {...(canManagePhotos && refilingPhotoId === photo.photoId
                ? {
                    filingPicker: (
                      <FilingPicker
                        targets={[...filingTargets]}
                        busy={photoActionBusyId === photo.photoId}
                        onCancel={() => onToggleRefile?.(photo.photoId)}
                        onPick={(promptKey) => {
                          void onRefilePhoto?.(photo.photoId, promptKey);
                        }}
                      />
                    ),
                  }
                : {})}
            />
          ))}
        </div>
      )}
      {onUploadPhoto && (
        <ReportPhotoEditor
          jobId={jobId}
          promptKey={`clause:${clauseId}`}
          photos={[]}
          uploadOnly
          onOpenPhoto={onOpenPhoto}
          onUploadPhoto={onUploadPhoto}
        />
      )}
      <div className="form-grid">
        <label>
          Assessment
          <select
            value={selected}
            onChange={(e) => {
              const value = e.target.value;
              if (!value) return;
              const option = options.find((candidate) => candidate.value === value);
              if (!option) return;
              onChange(
                applyAssessment(
                  response ?? { section, clauseId, outcome: 'Compliant', photoIds: [] },
                  option.value,
                ),
              );
            }}
          >
            <option value="">Select…</option>
            {selected && !options.some((option) => option.value === selected) && (
              <option value={selected}>
                Previously recorded: {selected} (not currently offered)
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {portalAssessmentLabel(option.value, option.label)}
                {option.requiresPhoto ? ' · photo required' : ''}
              </option>
            ))}
          </select>
        </label>
      </div>
      {needsWording && (
        <>
          {wordingOptions.length > 0 && (
            <div className="edit-clause-wording">
              <span className="muted">Canned wording</span>
              <div className="edit-clause-wording__options">
                {wordingOptions.map((text) => {
                  const active = (response?.recommendation ?? '').includes(text);
                  return (
                    <button
                      key={text}
                      type="button"
                      className={active ? 'is-active' : undefined}
                      onClick={() =>
                        onPatch((current) => {
                          const currentText = current.recommendation ?? '';
                          const next = active
                            ? currentText
                                .replace(text, '')
                                .replace(/\n{3,}/g, '\n\n')
                                .trim()
                            : [currentText.trim(), text].filter(Boolean).join('\n\n');
                          return { ...current, recommendation: next };
                        })
                      }
                    >
                      {text.length > 120 ? `${text.slice(0, 117)}…` : text}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <label>
            Recommendation / wording
            <textarea
              className={customResponse ? 'custom-response' : undefined}
              rows={3}
              value={response?.recommendation ?? ''}
              onChange={(e) =>
                onPatch((current) => ({ ...current, recommendation: e.target.value }))
              }
            />
            {customResponse && <small className="custom-response__note">Custom Response</small>}
          </label>
        </>
      )}
      <label>
        Inspector note
        <textarea
          rows={2}
          disabled={!response}
          value={response?.inspectorNote ?? ''}
          onChange={(e) => onPatch((current) => ({ ...current, inspectorNote: e.target.value }))}
        />
      </label>
    </div>
  );
}
